import { HttpErrorResponse } from '@angular/common/http';

const SERVER_ANSWER_STATUSES = new Set([401, 403, 404, 408, 422, 429, 500, 502, 503, 504]);

export function isNetworkFailure(error: unknown): boolean {
    if (hasServerAnswerStatus(error)) {
        return false;
    }

    const combined = toCombinedErrorText(error);
    if (isCancelledRequest(combined) || isChunkLoadFailure(combined)) {
        return false;
    }

    return isUnreachableRequest(error, combined);
}

export function toSafeNetworkHttpError(original: HttpErrorResponse): HttpErrorResponse {
    return new HttpErrorResponse({
        error: {
            error: {
                messages: [],
                statusCode: 0,
            },
        },
        status: 0,
        statusText: original.statusText || 'Unknown Error',
        url: original.url,
    });
}

function hasServerAnswerStatus(error: unknown): boolean {
    const status = getErrorStatus(error);
    return status !== undefined && SERVER_ANSWER_STATUSES.has(status);
}

function isUnreachableRequest(error: unknown, combined: string): boolean {
    return hasHttpStatusZero(error) || hasProgressEventBody(error) || matchesNetworkFailureText(combined);
}

function hasHttpStatusZero(error: unknown): boolean {
    return getErrorStatus(error) === 0;
}

function getErrorStatus(error: unknown): number | undefined {
    if (error instanceof HttpErrorResponse) {
        return error.status;
    }
    if (typeof error === 'object' && error !== null && typeof (error as { status?: unknown }).status === 'number') {
        return (error as { status: number }).status;
    }
    return undefined;
}

function hasProgressEventBody(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
        return false;
    }
    const body = (error as { error?: unknown }).error;
    return typeof ProgressEvent !== 'undefined' && body instanceof ProgressEvent;
}

function toCombinedErrorText(error: unknown): string {
    if (error == null) {
        return '';
    }
    if (typeof error === 'string') {
        return error;
    }
    if (typeof error !== 'object') {
        return String(error);
    }
    const err = error as { name?: unknown; message?: unknown; toString?: () => string };
    const name = typeof err.name === 'string' ? err.name : '';
    const message = typeof err.message === 'string' ? err.message : '';
    const asString = typeof err.toString === 'function' ? err.toString() : '';
    return `${name} ${message} ${asString}`;
}

function isCancelledRequest(combined: string): boolean {
    const text = combined.toLowerCase();
    return (
        text.includes('http cancelled') ||
        text.includes('aborterror') ||
        text.includes('canceled') ||
        text.includes('cancelled')
    );
}

function isChunkLoadFailure(combined: string): boolean {
    const text = combined.toLowerCase();
    return (
        text.includes('chunkloaderror') ||
        text.includes('loading chunk') ||
        text.includes('dynamically imported module') ||
        text.includes('failed to load module script')
    );
}

function matchesNetworkFailureText(combined: string): boolean {
    const text = combined.toLowerCase();
    if (text.includes('failed to fetch') && !text.includes('dynamically imported module')) {
        return true;
    }
    return (
        text.includes('networkerror') ||
        text.includes('err_internet_disconnected') ||
        text.includes('err_network_changed') ||
        text.includes('net::err_')
    );
}
